/**
 * ============================================================
 * COMPONENT: NewsWithSidebar (Premium Edition v5.0)
 * DESIGN: Modern gradients, smooth animations, full responsiveness
 * ============================================================
 */

import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import SectionHeader from "./SectionHeader";
import TagCloud from "./TagCloud";
import PopularList from "./PopularList";
import PostMeta from "./PostMeta";
import { imgOrPlaceholder } from "../utils/format";
import { useTranslation } from "../i18n";
import { withLangPrefix } from "../lib/i18nRoutes";

// Design Tokens
const DESIGN = {
  colors: {
    primary: "#6366f1", 
    secondary: "#8b5cf6", 
    accent: "#ec4899",
    success: "#10b981", 
    bg: "#ffffff", 
    bgAlt: "#f8fafc", 
    text: "#0f172a",
    textLight: "#64748b", 
    border: "#e2e8f0",
    gradientPrimary: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    gradientSunset: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
  },
  radius: { 
    sm: "8px", 
    md: "12px", 
    lg: "16px", 
    xl: "24px", 
    "2xl": "32px", 
    full: "9999px" 
  },
  shadows: {
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
    "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
  }
};

// Data processor
const useArticleProcessor = (articles, lang) => {
  return useMemo(() => {
    if (!articles?.length) return [];
    const L = (lang || "en").split(/[-_]/)[0].toLowerCase();
    return articles.map((a, i) => {
      let title = a.title || "";
      let excerpt = a.excerpt || "";
      
      if (a.translations?.length) {
        const tr = a.translations.find(t => t?.target_lang?.toLowerCase().startsWith(L));
        if (tr?.title) title = tr.title;
        if (tr?.excerpt) excerpt = tr.excerpt;
      } else if (a.translated?.[L]) {
        title = a.translated[L].title || title;
        excerpt = a.translated[L].excerpt || excerpt;
      }

      return {
        ...a,
        _id: a.id || i,
        _title: title.replace(/\s*Translated\s*\(.*?\)\s*$/i, "").trim(),
        _excerpt: excerpt.replace(/\s*Translated\s*\(.*?\)\s*$/i, "").trim(),
        _category: a.categories?.[0]?.name || a.category || "Featured",
        _img: a.image || a.thumbnail || `https://picsum.photos/seed/${a.id}/800/600`,
        _href: withLangPrefix(`/articles/${encodeURIComponent(a.slug || a.id || `post-${i}`)}`, lang),
        _author: a.author?.username || "Editorial Staff"
      };
    });
  }, [articles, lang]);
};

// Progress Bar
const SectionProgress = () => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const el = document.getElementById("canvas-news");
      if (el) {
        const rect = el.getBoundingClientRect();
        setWidth(Math.max(0, Math.min(100, ((-rect.top) / (rect.height - window.innerHeight)) * 100)));
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return (
    <div style={{
      position: "fixed", 
      top: 0, 
      left: 0, 
      width: "100%", 
      height: "4px", 
      zIndex: 1000,
      background: "transparent"
    }}>
      <div style={{
        height: "100%", 
        background: DESIGN.colors.gradientPrimary, 
        width: `${width}%`,
        transition: "width 0.15s ease-out", 
        boxShadow: `0 0 20px ${DESIGN.colors.primary}`
      }} />
      <div style={{
        position: "absolute",
        top: 0,
        right: 0,
        width: "100px",
        height: "100%",
        background: DESIGN.colors.gradientSunset,
        filter: "blur(20px)",
        opacity: 0.5
      }} />
    </div>
  );
};

// Action Buttons
const ActionButtons = ({ url, title }) => {
  const [copied, setCopied] = useState(false);
  const handleShare = (e) => {
    e.preventDefault();
    if (navigator.share) {
      navigator.share({ title, url });
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  return (
    <div style={{ 
      position: "absolute", 
      top: "20px", 
      right: "20px", 
      zIndex: 10, 
      display: "flex", 
      gap: "12px" 
    }}>
      <button 
        onClick={handleShare} 
        title={copied ? "Copied!" : "Share"}
        style={{
          width: "50px", 
          height: "50px", 
          borderRadius: "50%", 
          background: "rgba(255,255,255,0.95)",
          border: "none", 
          cursor: "pointer", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          color: DESIGN.colors.text, 
          transition: "all 0.3s", 
          boxShadow: DESIGN.shadows.lg, 
          padding: 0
        }} 
        onMouseEnter={e => e.target.style.transform = "scale(1.1) translateY(-2px)"} 
        onMouseLeave={e => e.target.style.transform = "scale(1)"}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="18" cy="5" r="3"></circle>
          <circle cx="6" cy="12" r="3"></circle>
          <circle cx="18" cy="19" r="3"></circle>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
        </svg>
      </button>
      <button 
        title="Save"
        style={{
          width: "50px", 
          height: "50px", 
          borderRadius: "50%", 
          background: "rgba(255,255,255,0.95)",
          border: "none", 
          cursor: "pointer", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          color: DESIGN.colors.text, 
          transition: "all 0.3s", 
          boxShadow: DESIGN.shadows.lg, 
          padding: 0
        }}
        onMouseEnter={e => e.target.style.transform = "scale(1.1) translateY(-2px)"} 
        onMouseLeave={e => e.target.style.transform = "scale(1)"}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
          <polyline points="17 21 17 13 7 13 7 21"></polyline>
          <polyline points="7 3 7 8 15 8"></polyline>
        </svg>
      </button>
    </div>
  );
};

// Spotlight Card
const SpotlightCard = ({ a, lang, index }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{
      animation: "fadeInUp 0.6s ease-out forwards", 
      opacity: 0, 
      animationDelay: `${index * 0.1}s`
    }}>
      <Link to={a._href} style={{ textDecoration: "none", color: "inherit", display: "block" }}>
        <div style={{
          position: "relative", 
          height: "280px", 
          borderRadius: DESIGN.radius.lg, 
          overflow: "hidden",
          marginBottom: "15px", 
          boxShadow: DESIGN.shadows.lg
        }} 
        onMouseEnter={() => setHovered(true)} 
        onMouseLeave={() => setHovered(false)}>
          <img 
            src={imgOrPlaceholder(a._img, 600, 400)} 
            alt={a._title} 
            loading="lazy"
            style={{
              width: "100%", 
              height: "100%", 
              objectFit: "cover",
              transform: hovered ? "scale(1.1)" : "scale(1)", 
              transition: "transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)"
            }} 
          />
          <div style={{
            position: "absolute", 
            inset: 0,
            background: "linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)",
            opacity: hovered ? 1 : 0, 
            transition: "opacity 0.4s", 
            display: "flex", 
            alignItems: "flex-end", 
            padding: "15px"
          }}>
            <span style={{
              display: "inline-block", 
              background: DESIGN.colors.accent, 
              color: "white",
              padding: "5px 12px", 
              borderRadius: DESIGN.radius.sm, 
              fontSize: "0.65rem",
              fontWeight: 700, 
              textTransform: "uppercase"
            }}>
              {a._category}
            </span>
          </div>
        </div>
        <h3 style={{ 
          fontSize: "1.4rem", 
          fontWeight: 800, 
          lineHeight: 1.3, 
          marginBottom: "8px",
          margin: "0 0 8px 0"
        }}>
          {a._title}
        </h3>
        <p style={{ 
          fontSize: "0.95rem", 
          color: DESIGN.colors.textLight, 
          marginBottom: "12px", 
          lineHeight: 1.5,
          margin: "0 0 12px 0"
        }}>
          {a._excerpt?.slice(0, 80)}...
        </p>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "10px", 
          fontSize: "0.85rem", 
          color: DESIGN.colors.textLight 
        }}>
          <PostMeta date={a.published_at} locale={lang} />
          <span style={{ 
            paddingLeft: "10px", 
            borderLeft: `1px solid ${DESIGN.colors.border}` 
          }}>
            4 min read
          </span>
        </div>
      </Link>
    </div>
  );
};

// Minimal Row
const MinimalRow = ({ a, lang, index }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div style={{
      padding: "28px 0", 
      borderBottom: `1px solid ${DESIGN.colors.border}`,
      animation: "fadeInLeft 0.5s ease-out forwards", 
      opacity: 0, 
      animationDelay: `${index * 0.05}s`
    }} 
    onMouseEnter={() => setHovered(true)} 
    onMouseLeave={() => setHovered(false)}>
      <Link to={a._href} style={{
        textDecoration: "none", 
        color: "inherit", 
        display: "flex", 
        gap: hovered ? "28px" : "24px",
        alignItems: "flex-start", 
        transition: "gap 0.3s"
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ 
            fontSize: "0.7rem", 
            fontWeight: 800, 
            color: DESIGN.colors.primary, 
            textTransform: "uppercase", 
            marginBottom: "6px" 
          }}>
            {a._category}
          </div>
          <h4 style={{ 
            fontSize: "1.25rem", 
            fontWeight: 700, 
            lineHeight: 1.3, 
            marginBottom: "10px", 
            color: hovered ? DESIGN.colors.primary : DESIGN.colors.text, 
            transition: "color 0.3s",
            margin: "0 0 10px 0"
          }}>
            {a._title}
          </h4>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "8px", 
            fontSize: "0.85rem", 
            color: DESIGN.colors.textLight 
          }}>
            <span style={{ fontWeight: 500 }}>{a._author}</span>
            <span style={{ opacity: 0.5 }}>•</span>
            <PostMeta date={a.published_at} locale={lang} />
          </div>
        </div>
        <div style={{
          position: "relative", 
          width: "150px", 
          height: "150px", 
          borderRadius: DESIGN.radius.lg,
          overflow: "hidden", 
          flexShrink: 0, 
          boxShadow: DESIGN.shadows.lg
        }}>
          <img 
            src={imgOrPlaceholder(a._img, 200, 200)} 
            alt={a._title} 
            loading="lazy"
            style={{
              width: "100%", 
              height: "100%", 
              objectFit: "cover",
              transform: hovered ? "scale(1.1)" : "scale(1)", 
              transition: "transform 0.5s"
            }} 
          />
          <div style={{
            position: "absolute", 
            inset: 0,
            background: "linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)",
            opacity: hovered ? 1 : 0, 
            transition: "opacity 0.3s"
          }} />
        </div>
      </Link>
    </div>
  );
};

// Newsletter Widget
// Newsletter Widget
const API_BASE       = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000/api";

const NewsletterWidget = ({ t, newsletterEndpoint = `${API_BASE}/subscribe/` }) => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const trimmed = email.trim();

    if (!validateEmail(trimmed)) {
      setError(t("invalid_email") || "Please enter a valid email address.");
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(newsletterEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: trimmed,
        }),
      });

      if (!res.ok) {
        let message = t("newsletter_failed") || "Subscription failed. Please try again.";
        try {
          const data = await res.json();
          if (data?.message) message = data.message;
        } catch {}
        throw new Error(message);
      }

      setSubmitted(true);
      setEmail("");
      setTimeout(() => setSubmitted(false), 3000);
    } catch (err) {
      setError(err.message || (t("newsletter_failed") || "Subscription failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        background: DESIGN.colors.bg,
        border: `2px solid ${DESIGN.colors.border}`,
        padding: "40px",
        borderRadius: DESIGN.radius.lg,
        transition: "all 0.3s",
        animation: "slideUp 0.6s ease-out",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "-100px",
          right: "-100px",
          width: "300px",
          height: "300px",
          background: DESIGN.colors.gradientSunset,
          borderRadius: "50%",
          filter: "blur(80px)",
          opacity: 0.15,
          zIndex: 0,
        }}
      />

      <div style={{ position: "relative", zIndex: 1 }}>
        <h4
          style={{
            fontSize: "1.4rem",
            fontWeight: 800,
            margin: "0 0 12px 0",
            color: DESIGN.colors.text,
          }}
        >
          {t("the_dispatch") || "The Weekly Dispatch"}
        </h4>

        <p
          style={{
            fontSize: "0.95rem",
            color: DESIGN.colors.textLight,
            margin: "0 0 20px 0",
            lineHeight: 1.5,
          }}
        >
          {t("newsletter_msg") || "Get the most important news delivered to your inbox weekly."}
        </p>

        <form onSubmit={handleSubmit} style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
          <input
            type="email"
            placeholder={t("email_placeholder") || "Enter your email"}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            required
            style={{
              flex: 1,
              padding: "12px 16px",
              border: `1px solid ${error ? "#ef4444" : DESIGN.colors.border}`,
              borderRadius: DESIGN.radius.md,
              fontSize: "0.95rem",
              transition: "all 0.3s",
              outline: "none",
              fontFamily: "inherit",
              opacity: loading ? 0.75 : 1,
            }}
            onFocus={(e) => {
              e.target.style.borderColor = DESIGN.colors.primary;
              e.target.style.boxShadow = "0 0 0 3px rgba(99, 102, 241, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = error ? "#ef4444" : DESIGN.colors.border;
              e.target.style.boxShadow = "none";
            }}
          />

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "44px",
              height: "44px",
              background: submitted ? DESIGN.colors.success : DESIGN.colors.primary,
              color: "white",
              border: "none",
              borderRadius: DESIGN.radius.md,
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s",
              flexShrink: 0,
              opacity: loading ? 0.8 : 1,
            }}
            onMouseEnter={(e) => {
              if (loading) return;
              e.currentTarget.style.background = submitted ? DESIGN.colors.success : DESIGN.colors.secondary;
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 8px 20px rgba(99, 102, 241, 0.3)";
            }}
            onMouseLeave={(e) => {
              if (loading) return;
              e.currentTarget.style.background = submitted ? DESIGN.colors.success : DESIGN.colors.primary;
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            {loading ? (
              <span
                style={{
                  width: 18,
                  height: 18,
                  border: "2px solid white",
                  borderTop: "2px solid transparent",
                  borderRadius: "50%",
                  display: "inline-block",
                  animation: "spin 0.8s linear infinite",
                }}
              />
            ) : submitted ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            )}
          </button>
        </form>

        {error && (
          <p style={{ fontSize: "0.85rem", color: "#ef4444", margin: "0 0 10px 0" }}>
            {error}
          </p>
        )}

        <p style={{ fontSize: "0.8rem", color: DESIGN.colors.textLight, margin: 0 }}>
          No spam. Unsubscribe anytime.
        </p>
      </div>
    </div>
  );
};

// Main Component
const NewsWithSidebar = ({ 
  title = "The News Canvas", 
  posts = [], 
  tags = [], 
  popular = [], 
  initialCount = 6, 
  loadMoreStep = 4 
}) => {
  const { lang, t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(initialCount);
  const [isZooming, setIsZooming] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const allArticles = useArticleProcessor(posts, lang);
  const visible = useMemo(() => allArticles.slice(0, visibleCount), [allArticles, visibleCount]);
  
  const hero = visible[0];
  const grid = visible.slice(1, 3);
  const feed = visible.slice(3);

  const handleLoadMore = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setVisibleCount(v => v + loadMoreStep);
      setLoadingMore(false);
    }, 500);
  };

  if (!posts?.length) return null;
  
  const isRTL = lang === 'ar';

  return (
    <section style={{ 
      background: DESIGN.colors.bg, 
      color: DESIGN.colors.text, 
      fontFamily: "'Inter', 'Segoe UI', sans-serif", 
      overflow: "hidden", 
      padding: 0, 
      margin: 0, 
      direction: isRTL ? "rtl" : "ltr" 
    }} 
    id="canvas-news">
      <SectionProgress />
      
      <div style={{ maxWidth: "1320px", margin: "0 auto", padding: "0 20px" }}>
        
        {/* ===== HEADER SECTION ===== */}
        <div style={{ padding: "60px 0 40px", animation: "fadeInDown 0.6s ease-out" }}>
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            marginBottom: "30px", 
            flexWrap: "wrap", 
            gap: "20px" 
          }}>
            <SectionHeader title={t("latest_stories") || "Latest Stories"} />
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "10px", 
              padding: "10px 18px", 
              background: DESIGN.colors.bgAlt, 
              border: `1px solid ${DESIGN.colors.border}`, 
              borderRadius: DESIGN.radius.full, 
              animation: "slideInRight 0.6s ease-out" 
            }}>
              <span style={{ 
                width: "8px", 
                height: "8px", 
                background: DESIGN.colors.success, 
                borderRadius: "50%", 
                animation: "pulse 2s infinite", 
                boxShadow: `0 0 10px ${DESIGN.colors.success}` 
              }} />
              <span style={{ 
                fontSize: "0.85rem", 
                fontWeight: 600, 
                color: DESIGN.colors.textLight 
              }}>
                {t("live_updates") || "Live Updates"}
              </span>
            </div>
          </div>
          <div style={{ 
            height: "1px", 
            background: `linear-gradient(90deg, transparent, ${DESIGN.colors.border}, transparent)` 
          }} />
        </div>

        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "1fr 380px", 
          gap: "40px", 
          marginBottom: "80px" 
        }}>
          
          {/* ===== MAIN CONTENT ===== */}
          <main>
            
            {/* ===== HERO SECTION (FIXED) ===== */}
            {hero && (
              <article style={{ 
                marginBottom: "50px", 
                animation: "fadeIn 0.8s ease-out" 
              }}>
                <Link 
                  to={hero._href} 
                  style={{ 
                    textDecoration: "none", 
                    color: "inherit", 
                    display: "block",
                    position: "relative"
                  }}
                  onMouseEnter={() => setIsZooming(true)} 
                  onMouseLeave={() => setIsZooming(false)}
                >
                  {/* Image Container */}
                  <div style={{
                    position: "relative", 
                    height: "500px", 
                    borderRadius: DESIGN.radius["2xl"], 
                    overflow: "hidden",
                    marginBottom: "30px", 
                    boxShadow: DESIGN.shadows["2xl"]
                  }}>
                    
                    {/* Background Image */}
                    <img 
                      src={imgOrPlaceholder(hero._img, 1200, 800)} 
                      alt={hero._title}
                      style={{
                        width: "100%", 
                        height: "100%", 
                        objectFit: "cover",
                        transform: isZooming ? "scale(1.12)" : "scale(1)", 
                        transition: "transform 1.2s cubic-bezier(0.34, 1.56, 0.64, 1)"
                      }} 
                    />
                    
                    {/* Gradient Overlay */}
                    <div style={{
                      position: "absolute", 
                      inset: 0,
                      background: "linear-gradient(0deg, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.6) 40%, transparent 100%)", 
                      zIndex: 1
                    }} />
                    
                    {/* Action Buttons */}
                    <ActionButtons url={hero._href} title={hero._title} />

                    {/* HERO CONTENT - NOW INSIDE IMAGE CONTAINER */}
                    <div style={{ 
                      position: "absolute", 
                      bottom: 0, 
                      left: 0, 
                      width: "100%", 
                      padding: "40px", 
                      color: "white", 
                      zIndex: 5 
                    }}>
                      
                      {/* Category & Date */}
                      <div style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "15px", 
                        marginBottom: "15px", 
                        flexWrap: "wrap" 
                      }}>
                        <span style={{ 
                          background: DESIGN.colors.primary, 
                          color: "white", 
                          padding: "6px 16px", 
                          borderRadius: DESIGN.radius.full, 
                          fontSize: "0.75rem", 
                          fontWeight: 700, 
                          textTransform: "uppercase", 
                          boxShadow: `0 4px 15px rgba(99, 102, 241, 0.4)` 
                        }}>
                          {hero._category}
                        </span>
                        <span style={{ 
                          fontSize: "0.9rem", 
                          color: "rgba(255,255,255,0.8)" 
                        }}>
                          <PostMeta date={hero.published_at} locale={lang} />
                        </span>
                      </div>
                      
                      {/* Title */}
                      
                      
                      {/* Excerpt */}
                      <p style={{ 
                        fontSize: "1.1rem", 
                        lineHeight: 1.6, 
                        color: "rgba(255,255,255,0.95)", 
                        maxWidth: "600px", 
                        marginBottom: "25px",
                        margin: "0 0 25px 0"
                      }}>
                        {hero._excerpt}
                      </p>
                      
                      {/* Author Card */}
                      <div style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "12px", 
                        marginTop: "25px" 
                      }}>
                        
                        <div style={{ 
                          display: "flex", 
                          flexDirection: "column", 
                          gap: "2px" 
                        }}>
                          
                          
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </article>
            )}

            {/* ===== SPOTLIGHT GRID ===== */}
            {grid.length > 0 && (
              <section style={{ marginBottom: "50px" }}>
                <div style={{ 
                  display: "grid", 
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
                  gap: "24px" 
                }}>
                  {grid.map((a, idx) => (
                    <SpotlightCard key={a._id} a={a} lang={lang} index={idx} />
                  ))}
                </div>
              </section>
            )}

            {/* ===== EDITORIAL FEED ===== */}
            {feed.length > 0 && (
              <section style={{ marginBottom: "50px" }}>
                <div style={{ marginBottom: "30px" }}>
                  <h2 style={{ 
                    fontSize: "1.8rem", 
                    fontWeight: 800, 
                    marginBottom: "15px", 
                    color: DESIGN.colors.text,
                    margin: "0 0 15px 0"
                  }}>
                    {t("more_stories") || "More Stories"}
                  </h2>
                  <div style={{ 
                    height: "3px", 
                    width: "60px", 
                    background: DESIGN.colors.gradientPrimary, 
                    borderRadius: DESIGN.radius.full 
                  }} />
                </div>
                <div style={{ borderTop: `1px solid ${DESIGN.colors.border}` }}>
                  {feed.map((a, idx) => (
                    <MinimalRow key={a._id} a={a} lang={lang} index={idx} />
                  ))}
                </div>
              </section>
            )}

            {/* ===== LOAD MORE BUTTON ===== */}
            {visibleCount < allArticles.length && (
              <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                <button 
                  onClick={handleLoadMore} 
                  disabled={loadingMore}
                  style={{
                    padding: "15px 50px", 
                    fontSize: "1rem", 
                    fontWeight: 700, 
                    letterSpacing: "0.5px",
                    textTransform: "uppercase", 
                    border: `2px solid ${DESIGN.colors.primary}`,
                    background: loadingMore ? DESIGN.colors.primary : "transparent", 
                    color: loadingMore ? "white" : DESIGN.colors.primary,
                    borderRadius: DESIGN.radius.full, 
                    cursor: loadingMore ? "not-allowed" : "pointer",
                    transition: "all 0.3s", 
                    display: "inline-flex", 
                    alignItems: "center", 
                    gap: "10px",
                    opacity: loadingMore ? 0.6 : 1
                  }}
                  onMouseEnter={e => {
                    if (!loadingMore) {
                      e.target.style.background = DESIGN.colors.primary;
                      e.target.style.color = "white";
                      e.target.style.boxShadow = "0 10px 30px rgba(99, 102, 241, 0.3)";
                    }
                  }}
                  onMouseLeave={e => {
                    if (!loadingMore) {
                      e.target.style.background = "transparent";
                      e.target.style.color = DESIGN.colors.primary;
                      e.target.style.boxShadow = "none";
                    }
                  }}
                >
                  <span>
                    {loadingMore ? "Loading..." : (t("explore_more") || "Explore More")}
                  </span>
                  <svg 
                    width="18" 
                    height="18" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    style={{ animation: "bounceDown 1s infinite" }}
                  >
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <polyline points="19 12 12 19 5 12"></polyline>
                  </svg>
                </button>
              </div>
            )}
          </main>

          {/* ===== SIDEBAR ===== */}
          <aside style={{ position: "relative" }}>
            <div style={{ 
              position: "sticky", 
              top: "100px", 
              display: "flex", 
              flexDirection: "column", 
              gap: "30px" 
            }}>
              
              {/* Trending Widget */}
              <div style={{ 
                background: DESIGN.colors.bgAlt, 
                border: `1px solid ${DESIGN.colors.border}`, 
                padding: "28px", 
                borderRadius: DESIGN.radius.lg, 
                transition: "all 0.3s" 
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = DESIGN.colors.primary;
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(99, 102, 241, 0.1)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = DESIGN.colors.border;
                e.currentTarget.style.boxShadow = "none";
              }}>
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "12px", 
                  marginBottom: "25px" 
                }}>
                  <span style={{ 
                    fontSize: "0.75rem", 
                    fontWeight: 900, 
                    background: DESIGN.colors.text, 
                    color: "white", 
                    width: "28px", 
                    height: "28px", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center", 
                    borderRadius: "50%" 
                  }}>
                    01
                  </span>
                  <h3 style={{ 
                    fontSize: "1.2rem", 
                    fontWeight: 800, 
                    margin: 0, 
                    color: DESIGN.colors.text 
                  }}>
                    {t("trending") || "Trending Now"}
                  </h3>
                </div>
                <PopularList items={popular} lang={lang} />
              </div>

              {/* Newsletter Widget */}
              <NewsletterWidget t={t} />

              {/* Topics Widget */}
              <div style={{ 
                background: DESIGN.colors.bgAlt, 
                border: `1px solid ${DESIGN.colors.border}`, 
                padding: "28px", 
                borderRadius: DESIGN.radius.lg, 
                transition: "all 0.3s" 
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = DESIGN.colors.primary;
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(99, 102, 241, 0.1)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = DESIGN.colors.border;
                e.currentTarget.style.boxShadow = "none";
              }}>
                <div style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "12px", 
                  marginBottom: "25px" 
                }}>
                  <span style={{ 
                    fontSize: "0.75rem", 
                    fontWeight: 900, 
                    background: DESIGN.colors.text, 
                    color: "white", 
                    width: "28px", 
                    height: "28px", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center", 
                    borderRadius: "50%" 
                  }}>
                    02
                  </span>
                  <h3 style={{ 
                    fontSize: "1.2rem", 
                    fontWeight: 800, 
                    margin: 0, 
                    color: DESIGN.colors.text 
                  }}>
                    {t("topics") || "Browse Topics"}
                  </h3>
                </div>
                <TagCloud tags={tags} lang={lang} />
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* ===== GLOBAL STYLES ===== */}
      <style>{`
      @keyframes spin {
  to { transform: rotate(360deg); }
}
        @keyframes fadeInDown { 
          from { opacity: 0; transform: translateY(-20px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        @keyframes slideInRight { 
          from { opacity: 0; transform: translateX(20px); } 
          to { opacity: 1; transform: translateX(0); } 
        }
        @keyframes fadeIn { 
          from { opacity: 0; transform: translateY(20px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        @keyframes fadeInUp { 
          from { opacity: 0; transform: translateY(30px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        @keyframes fadeInLeft { 
          from { opacity: 0; transform: translateX(-20px); } 
          to { opacity: 1; transform: translateX(0); } 
        }
        @keyframes pulse { 
          0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); } 
          50% { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); } 
        }
        @keyframes bounceDown { 
          0%, 100% { transform: translateY(0); } 
          50% { transform: translateY(4px); } 
        }
        @keyframes slideUp { 
          from { opacity: 0; transform: translateY(30px); } 
          to { opacity: 1; transform: translateY(0); } 
        }
        
        @media (max-width: 1200px) {
          [style*="grid-template-columns: 1fr 380px"] { 
            grid-template-columns: 1fr !important; 
          }
        }
        
        @media (max-width: 768px) {
          [style*="grid-template-columns: repeat(auto-fit"] { 
            grid-template-columns: 1fr !important; 
          }
          [style*="height: 500px"] {
            height: 350px !important;
          }
          [style*="grid-template-columns: 1fr 380px"] { 
            grid-template-columns: 1fr !important; 
          }
        }
        
        @media (max-width: 480px) {
          [style*="height: 500px"] {
            height: 280px !important;
          }
          [style*="padding: 40px"] {
            padding: 20px !important;
          }
          [style*="fontSize: clamp"] {
            font-size: 1.4rem !important;
          }
        }
      `}</style>
    </section>
  );
};

export default NewsWithSidebar;